import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PageHeader } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Textarea } from '../../components/ui/Input'
import { EmptyState } from '../../components/ui/EmptyState'
import { PermissionGate } from '../../components/PermissionGate'
import { useAuth } from '../../auth/AuthContext'
import { MessageBubble } from './MessageBubble'
import { NewChannelModal } from './NewChannelModal'
import { ChannelMembersModal } from './ChannelMembersModal'
import { useChatSession } from './useChatSession'
import { useAutoScrollToBottom } from './useAutoScrollToBottom'
import type { ChatMessage } from '../../api/types'
import styles from './Chat.module.css'

export function ChatPage() {
  const { me, hasPermission } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()

  const {
    channelsQuery,
    unreadQuery,
    selectedChannelId,
    setSelectedChannelId,
    messages,
    sendMessage,
    editMessage,
    deleteMessage,
    toggleReaction,
    notifyTyping,
    typingLabel,
  } = useChatSession()

  const [draft, setDraft] = useState('')
  const [replyTarget, setReplyTarget] = useState<ChatMessage | null>(null)
  const [showNewChannel, setShowNewChannel] = useState(false)
  const [showMembers, setShowMembers] = useState(false)
  const [showSidebarMobile, setShowSidebarMobile] = useState(true)
  const messagesRef = useRef<HTMLDivElement>(null)
  useAutoScrollToBottom(messagesRef, selectedChannelId, messages)

  // Deep-Link von Chat-Benachrichtigungen (?channel=<id>, siehe backend chat.service.ts
  // notifyChannelMembers). Läuft nach dem Hook-eigenen "ersten Channel automatisch wählen"
  // und überschreibt dessen Wahl bei Bedarf. Param wird nach Gebrauch entfernt, sonst würde
  // er einen späteren manuellen Channel-Wechsel per Sidebar-Klick wieder überschreiben.
  useEffect(() => {
    const channelFromUrl = searchParams.get('channel')
    if (channelFromUrl && channelsQuery.data?.some((c) => c.id === channelFromUrl)) {
      setSelectedChannelId(channelFromUrl)
      setShowSidebarMobile(false)
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, channelsQuery.data, setSelectedChannelId, setSearchParams])

  const handleSend = () => {
    if (!draft.trim() || !selectedChannelId) return
    sendMessage(draft, replyTarget?.id)
    setDraft('')
    setReplyTarget(null)
  }

  const selectedChannel = channelsQuery.data?.find((c) => c.id === selectedChannelId)

  if (channelsQuery.data?.length === 0) {
    return (
      <div>
        <PageHeader title="Teamchat" subtitle="Echtzeit-Kommunikation für das Entwicklerteam." />
        <EmptyState
          icon="💬"
          title="Noch keine Channels"
          description="Lege den ersten Channel an, z.B. Allgemein, Development, Testing oder Bugs."
          action={
            <PermissionGate anyOf={['chat.manage_channels']}>
              <Button variant="primary" onClick={() => setShowNewChannel(true)}>
                + Neuer Channel
              </Button>
            </PermissionGate>
          }
        />
        {showNewChannel ? <NewChannelModal onClose={() => setShowNewChannel(false)} onCreated={setSelectedChannelId} /> : null}
      </div>
    )
  }

  return (
    <div>
      <div className={styles.layout}>
        <div className={[styles.sidebar, showSidebarMobile ? styles.sidebarVisible : ''].join(' ')}>
          <div className={styles.sidebarHeader}>
            <span style={{ fontWeight: 700 }}>Teamchat</span>
            <PermissionGate anyOf={['chat.manage_channels']}>
              <button onClick={() => setShowNewChannel(true)} style={{ background: 'none', border: 'none', color: 'var(--accent-10)', cursor: 'pointer' }}>
                +
              </button>
            </PermissionGate>
          </div>
          <div className={styles.channelListLabel}>Channels</div>
          {channelsQuery.data?.map((channel) => (
            <div
              key={channel.id}
              className={[styles.channelItem, channel.id === selectedChannelId ? styles.channelItemActive : ''].join(' ')}
              onClick={() => {
                setSelectedChannelId(channel.id)
                setShowSidebarMobile(false)
              }}
            >
              # {channel.name}
              {unreadQuery.data?.[channel.id] ? <span className={styles.unreadDot}>{unreadQuery.data[channel.id]}</span> : null}
            </div>
          ))}
        </div>

        <div className={[styles.thread, !showSidebarMobile ? '' : styles.threadHidden].join(' ')}>
          <div className={styles.threadHeader}>
            <Button variant="ghost" size="sm" onClick={() => setShowSidebarMobile(true)} className={styles.backBtn}>
              ←
            </Button>
            <span style={{ flex: 1 }}># {selectedChannel?.name ?? '…'}</span>
            {selectedChannelId ? (
              <Button variant="ghost" size="sm" onClick={() => setShowMembers(true)}>
                Mitglieder
              </Button>
            ) : null}
          </div>
          <div className={styles.messages} ref={messagesRef}>
            {messages.map((message) => (
              <MessageBubble
                key={message.id}
                message={message}
                currentUserId={me!.user.id}
                canModerate={hasPermission('chat.manage_channels')}
                onReply={setReplyTarget}
                onEdit={editMessage}
                onDelete={deleteMessage}
                onToggleReaction={(msg, emoji) => toggleReaction(msg, emoji, me!.user.id)}
              />
            ))}
            {messages.length === 0 ? <EmptyState mascot title="Noch keine Nachrichten" description="Schreib die erste Nachricht in diesem Channel." /> : null}
          </div>
          <div className={styles.inputBar}>
            {typingLabel ? <div className={styles.typingIndicator}>{typingLabel}</div> : null}
            {replyTarget ? (
              <div className={styles.replyBar}>
                <span>Antwort an {replyTarget.author.displayName}: {replyTarget.body.slice(0, 60)}</span>
                <button onClick={() => setReplyTarget(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                  ✕
                </button>
              </div>
            ) : null}
            <div className={styles.inputRow}>
              <Textarea
                value={draft}
                onChange={(e) => {
                  setDraft(e.target.value)
                  notifyTyping()
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    handleSend()
                  }
                }}
                placeholder="Nachricht schreiben… (Enter zum Senden, Shift+Enter für Zeilenumbruch)"
                style={{ minHeight: 44 }}
              />
              <Button variant="primary" onClick={handleSend} disabled={!draft.trim()}>
                Senden
              </Button>
            </div>
          </div>
        </div>
      </div>

      {showNewChannel ? <NewChannelModal onClose={() => setShowNewChannel(false)} onCreated={setSelectedChannelId} /> : null}

      {showMembers && selectedChannelId ? (
        <ChannelMembersModal
          channelId={selectedChannelId}
          channelName={selectedChannel?.name ?? ''}
          onClose={() => setShowMembers(false)}
          onDeleted={() => {
            setShowMembers(false)
            setSelectedChannelId(null)
          }}
        />
      ) : null}
    </div>
  )
}
